/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import firebase from 'firebase/compat/app';
import { fancyFirebaseGetters, fancyFirebaseReferencer } from '@touchplan/fancy-referencer';
import { AngularFireDatabase, SnapshotAction } from '@angular/fire/compat/database';
import { Observable } from 'rxjs';
import { ITicketIndexData } from '../ticket-index-data';
import { TicketIndexLogger } from '../ticket-index-logger.service';

/**
 * Utility for creating a single, Firebase-level query against a Reference of either:
 *     - indices/ticketsTwo/{projectId}/planWide/{planId}, or
 *     - indices/ticketsTwo/{projectId}/projectWide
 * where the given "queryFunctionToApplyToRef" function applies the query operators to
 * that Reference.
 *
 * @param db Y'know, that thing
 * @param projectId The applicable project ID
 * @param planId The applicable plan ID, or a nullish value to request 'projectWide' data
 * @param queryFunctionToApplyToRef (Optional) Function that applies query operators to the Firebase ref
 */
export function ticketIndexQuerySnapshot$(
	db: AngularFireDatabase,
	logger: TicketIndexLogger,
	projectId: string,
	planId: string,
	queryFunctionToApplyToRef?: (ref: firebase.database.Reference) => firebase.database.Query
): Observable<SnapshotAction<ITicketIndexData>> {
	const indicesProjectIdPath = fancyFirebaseGetters.getIndicesTicketsTwoPath(projectId);
	let indexRef = fancyFirebaseReferencer.getLegacyRef<firebase.database.Reference>(
		indicesProjectIdPath,
		projectId
	);
	if (planId && typeof planId === 'string') {
		indexRef = indexRef.child('planWide').child(planId);
	} else {
		indexRef = indexRef.child('projectWide');
	}
	logger.logIndexStateChangeSubscribe(indexRef);
	return db.list<ITicketIndexData>(indexRef, queryFunctionToApplyToRef).stateChanges();
}
